import _ from "lodash"
import classNames from "classnames"
import React from "react"
import memoize from "memoize-weak"
import bind from "memoize-bind"

import BigCalendar, { withTimezoneAwareness } from "../../lib/react-big-calendar/index"
import { formatMoney } from "../../../shared/utils/socialtravel"

const WrappedBigCalendar = withTimezoneAwareness(BigCalendar)

const timeRangeFormat = ({ start, end }, culture, local) =>
  local.format(start, moment.TIME_FORMAT, culture) + " — " + local.format(end, moment.TIME_FORMAT, culture)
const timeRangeStartOnlyFormat = ({ start, end }, culture, local) =>
  local.format(start, moment.TIME_FORMAT, culture)
const dateRangeFormat = ({ start, end }, culture, local) =>
  local.format(start, (moment(start).isSame(end, 'month') ? 'D' : 'D MMMM') + (moment(start).isSame(end, 'year') ? '' : ' YYYY'), culture) +
    ' - ' + local.format(end, 'D MMMM YYYY', culture)
const weekRangeFormat = dateRangeFormat

const formats = {
  dateFormat: "DD",
  dayFormat: "ddd DD/MM",
  weekdayFormat: "ddd",
  selectRangeFormat: timeRangeFormat,
  eventTimeRangeFormat: timeRangeStartOnlyFormat,
  timeGutterFormat: moment.TIME_FORMAT,
  monthHeaderFormat: "MMMM YYYY",
  dayHeaderFormat: "dddd, D MMMM YYYY",
  dayRangeHeaderFormat: weekRangeFormat,
  agendaHeaderFormat: dateRangeFormat,
  agendaDateFormat: "ddd, D MMM",
  agendaTimeFormat: moment.TIME_FORMAT,
  agendaTimeRangeFormat: timeRangeFormat
}

const messages = {
  allDay: I18n.t("papp.components.SlotsCalendar.allDay"),
  previous: I18n.t("papp.components.SlotsCalendar.previous"),
  next: I18n.t("papp.components.SlotsCalendar.next"),
  today: I18n.t("papp.components.SlotsCalendar.today"),
  month: I18n.t("papp.components.SlotsCalendar.month"),
  week: I18n.t("papp.components.SlotsCalendar.week"),
  day: I18n.t("papp.components.SlotsCalendar.day"),
  agenda: I18n.t("papp.components.SlotsCalendar.agenda"),
  showMore: i => I18n.t("papp.components.SlotsCalendar.showMore", { count: i }),
  date: I18n.t("papp.components.SlotsCalendar.date"),
  time: I18n.t("papp.components.SlotsCalendar.time"),
  event: I18n.t("papp.components.SlotsCalendar.event"),
  slot_confirmed: I18n.t("papp.components.SlotsCalendar.slot_confirmed"),
}

const titleAccessor = slot => slot.product_variant_name ? `${slot.product_name} (${slot.product_variant_name})` : slot.product_name
const labelAccessor = slot => slot.display_as_confirmed ? messages.slot_confirmed : ''

const EventComponent = ({ event: slot }) => (
  <div className="event">
    <div className="event-content">
      <div className="event-name">
        {titleAccessor(slot)}
        <div className="event-label">
          {labelAccessor(slot)}
        </div>
      </div>
    </div>
  </div>
)

const MonthEventComponent = ({ event: slot }) => (
  <div className="month-event">
    {slot.includes_time && (
      <div className="month-event-time">
        {moment.parseZone(slot.starts_at).format(moment.TIME_FORMAT)}
      </div>
    )}
    <div className="month-event-content">
      <div className="month-event-name">
        {titleAccessor(slot)}
      </div>
    </div>
  </div>
)

const AgendaEventComponent = ({ className, onClick, ...props }) => (
  <div className={'rbc-event rbc-agenda-event ' + (className || '')} onClick={onClick}>
    <EventComponent {...props} />
  </div>
)

const ToolbarComponent = ({ label, views, view, onViewChange, onNavigate, messages }) => (
  <div className="rbc-toolbar">
    <span className="rbc-btn-group">
      <div className="btn-group">
        <a className="btn btn-default" onClick={() => onNavigate(BigCalendar.Navigate.TODAY)}>{messages.today}</a>
      </div>
      &nbsp;&nbsp;
      <div className="btn-group">
        <a className="btn btn-default btn-icon" onClick={() => onNavigate(BigCalendar.Navigate.PREVIOUS)}>
          <i className="icon fa fa-chevron-left"></i>
        </a>
        <a className="btn btn-default btn-icon" onClick={() => onNavigate(BigCalendar.Navigate.NEXT)}>
          <i className="icon fa fa-chevron-right"></i>
        </a>
      </div>
    </span>

    <span className="rbc-toolbar-label">{label}</span>

    <span className="rbc-btn-group">
      <div className="btn-group">
        {views.map(v => (
          <a key={v}
            className={view === v ? "btn btn-primary" : "btn btn-default"}
            onClick={() => onViewChange(v)}>
            {messages[v]}
          </a>
        ))}
      </div>
    </span>
  </div>
)

const getCalendarComponents = memoize((eventPropGetter, onSelectEvent) => ({
  event: EventComponent,
  month: {
    event: MonthEventComponent,
  },
  agenda: {
    event: props => <AgendaEventComponent onClick={bind(onSelectEvent, null, props.event)} {...eventPropGetter(props.event)} {...props} />,
  },
  toolbar: ToolbarComponent,
}))

const getEventPropGetter = memoize(modifiers => event => {
  return {
    className: classNames(_.mapValues(
      _.mapKeys(
        modifiers,
        (_, key) => `rbc-event--${key}`
      ),
      fn => fn(event)
    ))
  }
})

const views = ["month", "week", "day", "agenda"]

const mapSlotsToEvents = memoize(slots => {
  return slots.map(slot => ({
    ...slot,
    start: slot.starts_at,
    end: slot.ends_at,
    allDay: !slot.includes_time,
  }))
})

export default class SlotsCalendar extends React.PureComponent {
  constructor(...args) {
    super(...args)

    const firstAvailableSlot = this.props.slots.find(slot => slot.stock_available)
    const defaultDate = firstAvailableSlot ? new Date(firstAvailableSlot.starts_at) : new Date()
    const defaultView = this.props.defaultView || "month"

    this.state = {
      date: defaultDate,
      view: defaultView,
    }
  }

  handleSelectEvent(event) {
    const { onSlotSelect, slots } = this.props
    onSlotSelect && onSlotSelect(slots.find(sl => sl.id === event.id))
  }

  handleNavigate(date, view, action) {
    this.setState({ date }, () => {
      this.triggerNavigate()
    })
  }

  handleViewChange(view) {
    this.setState({ view }, () => {
      // this.triggerNavigate()
    })
  }

  triggerNavigate() {
    const { date, view } = this.state
    this.props.onNavigate && this.props.onNavigate({
      currentDate: date,
      minVisibleDate: moment(date).startOf("month").add(-7, "days").toDate(),
      maxVisibleDate: moment(date).endOf("month").add(7, "days").toDate(),
    })
  }

  render() {
    const {
      slots,
      timeZoneName,
      modifiers = {},
    } = this.props
    const { date, view } = this.state

    const onNavigate = bind(this.handleNavigate, this)
    const onSelectEvent = bind(this.handleSelectEvent, this)
    const onView = bind(this.handleViewChange, this)
    const eventPropGetter = getEventPropGetter(modifiers)
    const components = getCalendarComponents(eventPropGetter, onSelectEvent)

    const bigCalendarProps = {
      components,
      date,
      eventPropGetter,
      events: mapSlotsToEvents(slots),
      formats,
      messages,
      onNavigate,
      onSelectEvent,
      onView,
      popup: true,
      selectable: false,
      timeZoneName,
      titleAccessor,
      view,
      views,
    }

    return (
      <div style={{ position: "relative", height: "100%" }}>
        <WrappedBigCalendar {...bigCalendarProps} />
      </div>
    )
  }
}



// WEBPACK FOOTER //
// ./app/webpack/frontend/features/ProductsCalendarWidget/SlotsCalendar.jsx